import { Card, Typography, Avatar, CardActionArea, CardMedia } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useRouter } from 'next/router'
import React from 'react'

import { Post } from '../../../types/post'
import { urls } from '../../utils/config'
import { timeToRead } from '../../utils/string'
import { getPublishedDay } from '../../utils/post'
import Image from 'next/image'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '20px auto',
    display: 'flex',
    borderRadius: '0',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'visible',

    minHeight: '8rem',
    background: 'transparent',
    transition: 'transform .3s cubic-bezier(0.22, 0.61, 0.36, 1)',
    transform: 'translateY(0)',
    '@media (max-width: 420px)': {
      margin: '5px',
      padding: '20px',
    },
    '&:hover': {
      cursor: 'pointer',
      background: 'transparent',
      transform: 'translateY(-1rem)',
      transition: 'transform .3s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
    },
  },
  button: {
    margin: '20px 20px 0 0',
    width: '304px',
    height: '64px',
    fontSize: '18px',
  },
  contentContainer: {
    padding: '10px 0',
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'row',
    margin: '10px 0',
    flexWrap: 'wrap',
    alignItems: 'center',
    '& h6': {
      fontWeight: 600,
      fontSize: '.8rem',
    },
  },
  title: {
    fontSize: '1rem',
  },
  date: {
    color: '#7D7787',
    marginRight: '4px',
  },
  readTime: {
    color: '#C7C3CD',
    marginLeft: '4px',
  },
  authorContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '10px 0',
    alignItems: 'center',
    '& h6': {
      fontWeight: 600,
      fontSize: '.8rem',
      color: '#7D7787',
      marginLeft: '1rem',
    },
  },
  avatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  actionArea: {
    background: 'transparent',
    '&:hover $focusHighlight': {
      opacity: 0,
    },
  },
  focusHighlight: {},
  imgContainer: {
    height: '12rem',
    borderRadius: '.4rem',
    position: 'relative',
    overflow: 'hidden',
  },
  xs: {
    height: '12rem',
  },
  sm: {
    height: '12rem',
    [theme.breakpoints.down('xs')]: {
      height: '12rem',
    },
  },
  lg: {
    height: '24rem',
    [theme.breakpoints.down('xs')]: {
      height: '12rem',
    },
  },
}))

interface Props {
  post: Post
  type?: 'compact' | 'regular' | 'detailed'
  imgSize?: 'xs' | 'sm' | 'lg'
}

function ArticleCard({ post, type, imgSize }: Props): JSX.Element {
  const classes = useStyles({})
  const { push } = useRouter()
  const goToReview = () => push(`${urls[post.type]}/${post.slug}`)

  return (
    <Card className={classes.root} onClick={goToReview} elevation={0}>
      <CardActionArea
        classes={{
          root: classes.actionArea,
          focusHighlight: classes.focusHighlight,
        }}
        href={`/${post.type}/${post.slug}`}
        disableRipple
      >
        {type !== 'compact' && (
          <div className={`${classes.imgContainer} ${classes[imgSize || 'sm']}`}>
            <Image
              layout="fill"
              objectFit="cover"
              src={post.sharing_image?.url || post.banner?.url}
            />
          </div>
        )}
        <div className={classes.contentContainer}>
          <div className={classes.authorContainer}>
            <Avatar
              src={post.authors[0]?.photo.url}
              variant="circular"
              className={classes.avatar}
            />
            <Typography variant="subtitle2">{post.authors[0]?.name}</Typography>
          </div>
          <Typography variant="h5" className={classes.title}>
            {post.title}
          </Typography>
          <div className={classes.detailsContainer}>
            <Typography variant="subtitle2" className={classes.date}>
              {getPublishedDay(new Date(post.createdAt))}
            </Typography>
            ·
            <Typography variant="subtitle2" className={classes.readTime}>
              {timeToRead(post.text)} min read
            </Typography>
          </div>
        </div>
      </CardActionArea>
    </Card>
  )
}

export default ArticleCard
